import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Zap, BarChart3, ChevronRight, Search, Shield, TrendingUp, Target, Brain, RefreshCw, Clock, Eye, Volume2, Swords, CheckCircle, Rocket } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { ROUTES } from '../config/urls';

// Composants existants
import GoogleReviewsBox from '../components/GoogleReviewsBox';
import QuizCompletionCta from '../components/QuizCompletionCta';
import ExitPopupDemo from '../components/ExitPopupDemo';
import { CompanyLogo } from '../components/CompanyLogo';

// Services et hooks
import { useTestPageData } from '../features/testPage/hooks/useTestPageData';
import { useAICitations } from '../hooks/useAICitations';

// Types
interface Prospect {
  id?: string;
  prenom?: string;
  nom?: string;
  site_url?: string;
  score?: number;
  quiz_fait?: boolean;
  quiz_commence?: boolean;
  token?: string;
  metadata?: {
    company?: string;
    domain_emelia?: string;
    industry?: string;
    location?: string;
    companyDescription?: string;
  };
}

// Utilitaires
const extractDomain = (url: string): string => {
  if (!url) return '';
  try {
    if (url.startsWith('http')) {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, '');
    }
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('?')[0];
  } catch (error) {
    return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('?')[0];
  }
};

const getDomainFromProspect = (prospect: Prospect | null): string => {
  if (!prospect) return '';
  return prospect.site_url || prospect.metadata?.domain_emelia || '';
};

/* Utility function for formatting page speed scores - commented out as currently unused
const formatPageSpeedScore = (score: number | null | undefined, fallback: number = 0): number => {
  if (score === null || score === undefined) return fallback;
  if (score <= 1) return Math.round(score * 100);
  return Math.round(score);
};
*/

const splitIntoParagraphs = (text: string): string[] => {
  if (!text) return [];
  return text.split(/\n\s*\n/).filter(p => p.trim().length > 0).map(p => p.trim());
};

// Composant d'indicateur de progression de lecture
const ReadingProgressIndicator: React.FC = () => {
  const [progress, setProgress] = useState(0);

  React.useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', updateProgress);
    return () => window.removeEventListener('scroll', updateProgress);
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
      <div 
        className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// Composant CTA "Curiosity Gap" - Teasing de /monscore
const MidPageCTA: React.FC<{ token?: string; startQuiz?: () => void; navigate?: (path: string) => void }> = ({ token, startQuiz, navigate }) => {
  return (
    <motion.div
      className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-8 shadow-2xl overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      {/* Effets de fond */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-400/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-2xl mx-auto relative z-10">
        {/* Badge pulsant - Centré */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center px-3 py-1 bg-orange-500/20 border border-orange-400/30 rounded-full text-orange-300 text-xs font-medium animate-pulse">
            <Eye className="w-3 h-3 mr-1" />
            Aperçu exclusif
          </div>
        </div>

        <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 text-center">
          Finaliser votre diagnostic complet
        </h3>
        <p className="text-sm text-orange-300 text-center mb-4 font-medium">
          Analyse SmartIndex™ personnalisée — Valeur CHF 297.- offerte
        </p>

        {/* Liste teaser avec révélation progressive */}
        <div className="space-y-3 mb-6">
          <div className="flex items-start gap-3 bg-white/5 rounded-lg p-3 border border-white/10">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div className="text-gray-300 text-sm">
              <span className="font-semibold text-white">Votre SmartIndex™ personnalisé</span> (score de 0 à 100 basé sur 47 critères)
            </div>
          </div>
          <div className="flex items-start gap-3 bg-white/5 rounded-lg p-3 border border-white/10">
            <Target className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="text-gray-300 text-sm">
              <span className="font-semibold text-white">Les 3 opportunités #1</span> que vous ratez actuellement (avec impact chiffré)
            </div>
          </div>
          <div className="flex items-start gap-3 bg-white/5 rounded-lg p-3 border border-white/10">
            <Rocket className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-gray-300 text-sm">
              <span className="font-semibold text-white">Votre plan d'action priorisé</span> (quick wins + stratégie long terme)
            </div>
          </div>
        </div>

        <p className="text-orange-200 text-lg mb-6 text-center">
          <span className="font-bold">7 questions</span> pour passer de "diagnostic technique" à<br />
          <span className="font-bold text-orange-400">"feuille de route actionnable"</span> 🎯
        </p>

        {/* Bouton centré */}
        <div className="flex justify-center mb-6">
          <motion.button
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-xl px-10 py-5 rounded-xl shadow-lg hover:shadow-orange-500/50 transform transition-all duration-300 inline-flex items-center justify-center group"
            whileHover={{ scale: 1.02, boxShadow: "0 25px 50px -12px rgba(251, 146, 60, 0.5)" }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              if (token && startQuiz) {
                startQuiz();
              } else if (navigate) {
                navigate(ROUTES.QUIZ());
              }
            }}
          >
            <Brain className="mr-3 group-hover:rotate-12 transition-transform" size={24} />
            Débloquer l'analyse complète 🔓
            <ChevronRight className="ml-3 group-hover:translate-x-1 transition-transform" size={24} />
          </motion.button>
        </div>

        {/* Countdown subtil */}
        <div className="text-center">
          <p className="text-xs text-gray-400">
            Temps estimé : <span className="text-orange-400 font-medium">moins d'une minute</span>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * TestPage2 - Version optimisée UI/UX
 * Améliorations : Hiérarchie de conversion, contenu simplifié, mobile-first
 */
const TestPage2: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  // State for scroll progress (used in child components)
  const [_scrollProgress, _setScrollProgress] = useState(0);

  // Toujours utiliser le hook useTestPageData
  const {
    loading,
    prospect,
    pagespeedData,
    keywordsData,
    aiRecommendations,
    personalizedIntro,
    error,
    startQuiz,
    isLoadingIntro,
    isLoadingAi,
    keyComponentKey,
    mockData,
    refreshGoogleReviews
  } = useTestPageData();

  // Fetch AI citations
  const { citations: aiCitations, isLoading: isLoadingCitations } = useAICitations(prospect?.id);

  // Scroll vers le haut de la page au chargement (une seule fois)
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get site URL for display (moved up to use in fallback keywords)
  // Mock data pour la démo quand pas de token
  const mockProspect = {
    id: 'demo-id',
    prenom: "Démo",
    nom: "Entreprise",
    site_url: "demo-entreprise.fr",
    score: 68,
    quiz_fait: false,
    quiz_commence: false,
    token: 'demo',
    metadata: {
      company: "Démo Entreprise",
      domain_emelia: "demo-entreprise.fr",
      industry: "Services numériques",
      location: "France",
      companyDescription: "Une entreprise innovante spécialisée dans les solutions digitales"
    }
  };
  
  // Utiliser les vraies données si disponibles, sinon les données mock
  const currentProspect = prospect || mockProspect;
  
  const siteUrl = getDomainFromProspect(currentProspect);

  // Debug: Afficher les données de mots-clés récupérées

  // Fonction pour générer des mots-clés fallback basés sur l'entreprise
  const generateFallbackKeywords = () => {
    const companyName = currentProspect.metadata?.company || 'votre entreprise';
    const domain = siteUrl?.replace(/^https?:\/\/(www\.)?/, '').split('/')[0].split('.')[0] || '';
    const baseName = companyName.toLowerCase().replace(/\s+/g, '-');

    return [
      { keyword: `${baseName}`, volume: 320, position: 15, opportunity: "Forte", color: "green" },
      { keyword: `${baseName} suisse`, volume: 180, position: 22, opportunity: "Moyenne", color: "orange" },
      { keyword: `${baseName} services`, volume: 140, position: 28, opportunity: "Moyenne", color: "orange" },
      { keyword: `${baseName} avis`, volume: 90, position: null, opportunity: "Non positionné", color: "gray" },
      { keyword: `${domain || baseName} contact`, volume: 75, position: null, opportunity: "Non positionné", color: "gray" }
    ];
  };

  // Fonction pour transformer les données de mots-clés
  const transformKeywordsData = (keywords: any[]) => {
    if (!keywords || keywords.length === 0) {
      return generateFallbackKeywords();
    }

    return keywords.map((kw: any) => {
      // CORRECTION: Utiliser rank_absolute et traiter 0 comme "Non positionné"
      const rankAbsolute = kw.rank_absolute || kw.position || 0;
      const position = rankAbsolute === 0 ? null : rankAbsolute;
      
      let opportunity = "Non positionné";
      let color = "gray";

      if (position !== null) {
        if (position <= 10) {
          opportunity = "Acquis";
          color = "blue";
        } else if (position <= 20) {
          opportunity = "Forte";
          color = "green";
        } else if (position <= 30) {
          opportunity = "Moyenne";
          color = "orange";
        } else {
          opportunity = "Faible";
          color = "red";
        }
      }

      return {
        keyword: kw.keyword || 'Mot-clé inconnu',
        volume: kw.search_volume || kw.volume || 0,
        position: position,
        opportunity: opportunity,
        color: color
      };
    });
  };

  // Transformer les données pour l'affichage
  const displayKeywords = transformKeywordsData(keywordsData);

  // Pagination state for keywords
  const [currentPage, setCurrentPage] = useState(1);
  const keywordsPerPage = 5;
  const totalPages = Math.ceil(displayKeywords.length / keywordsPerPage);
  
  // Get current keywords for pagination
  const indexOfLastKeyword = currentPage * keywordsPerPage;
  const indexOfFirstKeyword = indexOfLastKeyword - keywordsPerPage;
  const currentKeywords = displayKeywords.slice(indexOfFirstKeyword, indexOfLastKeyword);

  // Rendu conditionnel pour les erreurs (seulement si token présent)
  if (token && error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Erreur</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Indicateur de progression de lecture */}
      <ReadingProgressIndicator />
      
      {/* Header optimisé mobile */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40 backdrop-blur-sm bg-white/95">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-center">
            <div className="flex flex-col items-center sm:flex-row sm:items-center sm:space-x-3 space-y-1 sm:space-y-0">
              <div className="flex-shrink-0">
                <img src="/images/smart_impact_logo-no-bg.png" alt="Smart Impact" className="h-10 object-contain" />
              </div>
              <div className="text-center">
                <p className="text-gray-600 text-xs font-medium">
                  Agence digitale • Référencement • IA
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section - Optimisé mobile */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 md:py-20 lg:py-24 overflow-hidden">
        {/* Background Elements - Réduits pour mobile */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-64 h-64 md:w-96 md:h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-48 h-48 md:w-80 md:h-80 bg-orange-400/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            
            {/* Titre optimisé mobile */}
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-6 leading-tight text-white">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                Découvrez comment <br />
                {currentProspect.metadata?.company || 'votre entreprise'} <br />
                se classe face aux PME romandes
              </span>
            </h1>
                
            {/* Introduction personnalisée - Compacte */}
            {isLoadingIntro ? (
              <div className="mx-auto mb-8 max-w-3xl text-center animate-pulse">
                <div className="h-4 bg-gray-600 rounded w-3/4 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-600 rounded w-full mx-auto mb-2"></div>
                <div className="h-4 bg-gray-600 rounded w-2/3 mx-auto"></div>
                <p className="text-sm text-gray-400 mt-3">Analyse en cours...</p>
              </div>
            ) : personalizedIntro ? (
              <div className="mx-auto mb-8 max-w-3xl text-center">
                {splitIntoParagraphs(personalizedIntro).slice(0, 2).map((paragraph: string, index: number) => (
                  <p key={index} className="mb-4 text-lg md:text-xl text-gray-300 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : (
              <p className="mx-auto mb-8 max-w-3xl text-lg md:text-xl text-gray-300 leading-relaxed text-center">
                Analyse digitale complète de votre présence en ligne.
              </p>
            )}
            
            {/* Site URL - Compact */}
            <div className="mb-8">
              <div className="inline-block bg-white/10 rounded-xl px-6 py-3 backdrop-blur-sm border border-white/20">
                <span className="font-mono text-xl md:text-2xl font-bold text-orange-400 select-none pointer-events-none">
                  {loading ? 'Chargement...' : 
                    error ? 'site-web.com' : 
                    getDomainFromProspect(currentProspect)
                  }
                </span>
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* Section Diagnostic Rapide */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Titre de section */}
            <div className="text-center mb-12">
              {/* Logo du client - Brandfetch API */}
              {siteUrl && <CompanyLogo siteUrl={siteUrl} className="mb-8" />}

              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  Votre <span className="text-orange-600">Performance Digitale</span>
                </h2>
                <div className="inline-flex items-center px-4 py-2 bg-orange-50 border border-orange-200 rounded-full mt-2">
                  <Brain className="w-4 h-4 mr-2 text-orange-600" />
                  <span className="text-sm font-medium text-orange-700">
                    SmartIndex™ en cours d'analyse
                  </span>
                </div>

                {/* Teaser progression */}
                <div className="mt-6 max-w-md mx-auto bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Analyse des données de base
                    </span>
                    <span className="text-green-600 font-medium">✓ Complète</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-2">
                      <Brain className="w-4 h-4 text-orange-600 animate-pulse" />
                      Calcul du SmartIndex™
                    </span>
                    <span className="text-orange-600 font-medium">⏳ Quiz requis</span>
                  </div>
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 italic text-center">
                      {currentProspect?.quiz_fait
                        ? "Votre SmartIndex™ personnalisé vous attend en fin de page 🎯"
                        : "Complétez le quiz en fin de page pour débloquer votre score complet 🚀"}
                    </p>
                  </div>
                </div>

                <p className="text-lg text-gray-600 max-w-3xl mx-auto italic mt-4">
                  L'algorithme de notation croisée qui mesure l'impact réel de votre présence digitale sur votre chiffre d'affaires.
                </p>
              </div>
              
            </div>

            {/* Grille diagnostic */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* PageSpeed - Version compacte */}
              <motion.div 
                className="bg-white rounded-3xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-2xl bg-orange-100 text-orange-600 mr-4">
                    <Zap size={20} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Vitesse de chargement</h3>
                    <p className="text-gray-600 text-sm">Performance de votre site</p>
                  </div>
                </div>
                
                {/* Scores simplifiés */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                        <BarChart3 className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Performance Mobile</h4>
                        <p className="text-2xl font-bold text-green-600">
                          {pagespeedData?.mobile_performance_score 
                            ? Math.round(pagespeedData.mobile_performance_score * 100) 
                            : mockData?.pageSpeed?.mobile || 65}%
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Temps de chargement correct mais améliorable</p>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
                        <BarChart3 className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Performance Desktop</h4>
                        <p className="text-2xl font-bold text-blue-600">
                          {pagespeedData?.desktop_performance_score 
                            ? Math.round(pagespeedData.desktop_performance_score * 100) 
                            : mockData?.pageSpeed?.desktop || 85}%
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">Excellentes performances sur ordinateur</p>
                  </div>
                </div>
                
                {/* Recommandation rapide */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-700">
                    <span className="font-semibold">Optimisation possible :</span> Améliorer la vitesse peut augmenter vos conversions de 20%
                  </p>
                </div>
              </motion.div>
              
              {/* Avis Google - Version compacte */}
              <motion.div 
                className="bg-white rounded-3xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="flex items-center mb-4">
                  <div 
                    className="p-3 rounded-2xl bg-orange-100 text-orange-600 cursor-pointer hover:scale-110 transition-transform mr-4"
                    onClick={() => {
                      // Forcer un nouveau rendu du composant GoogleReviewsBox avec une nouvelle clé
                      refreshGoogleReviews();
                    }}
                    title="Actualiser les avis Google"
                  >
                    <Star size={20} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Réputation en ligne</h3>
                    <p className="text-gray-600 text-sm">Avis clients Google</p>
                  </div>
                </div>
                
                <div className="min-h-[200px]">
                  {/* Monter le composant UNIQUEMENT quand le vrai prospect est chargé (évite l'ID de démo) */}
                  {prospect?.id ? (
                    <GoogleReviewsBox 
                      prospectId={prospect.id}
                      businessName={prospect.metadata?.company || 'Entreprise'}
                      location={prospect.metadata?.location}
                      domain={getDomainFromProspect(prospect)}
                      className="mb-8"
                      key={`google-reviews-${prospect.id}-${keyComponentKey}`}
                    />
                  ) : null}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Section SEO & Mots-clés */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Informations SEO */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analyse SEO
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Votre visibilité sur <span className="text-orange-600">Google Suisse</span>
                </h3>
                <p className="text-lg text-gray-600 mb-6 italic">
                  Découvrez combien de recherches mensuelles concernent votre activité et combien d'entre elles vous échappent encore. Vos mots-clés stratégiques, votre position moyenne et votre potentiel de visiteurs manquants.
                </p>
                
                {/* Points clés simplifiés */}
                <div className="space-y-5 mt-8">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Search className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">{displayKeywords.length} mots-clés détectés</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Potentiel d'amélioration élevé</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Target className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">+{476 * 3} visiteurs possibles/mois</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Volume2 className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">{displayKeywords.reduce((sum, kw) => sum + (kw.volume || 0), 0).toLocaleString()} volume total de recherche couvert</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Swords className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Niveau de concurrence {displayKeywords.length > 15 ? 'élevé' : displayKeywords.length > 8 ? 'moyen' : 'faible'}</span>
                  </div>
                </div>
              </motion.div>


              {/* Tableau mots-clés simplifié */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">Top mots-clés</h4>
                    <p className="text-sm text-gray-600">Positions dans Google Suisse</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-600">{displayKeywords.length}</div>
                    <div className="text-xs text-gray-500">Total détectés</div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {/* Debug: Afficher les mots-clés utilisés */}
                  {(() => {
                    return null;
                  })()}

                  {currentKeywords.map((metric, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 text-sm">{metric.keyword}</div>
                        <div className="text-xs text-gray-600">{metric.volume} recherches/mois</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          metric.position === null ? 'bg-gray-100 text-gray-600' :
                          metric.position <= 10 ? 'bg-green-100 text-green-800' : 
                          metric.position <= 30 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {metric.position === null ? 'Non classé' : `#${metric.position}`}
                        </span>
                        <span className={`text-xs font-medium ${
                          metric.color === 'gray' ? 'text-gray-600' :
                          metric.color === 'green' ? 'text-green-600' : 
                          metric.color === 'blue' ? 'text-blue-600' : 
                          metric.color === 'orange' ? 'text-orange-600' : 'text-red-600'
                        }`}>
                          {metric.opportunity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Keyword Pagination Component */}
                <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">Tous les mots clés ({displayKeywords.length})</p>
                    <div className="text-xs text-gray-500">Page {currentPage} sur {totalPages}</div>
                  </div>
                  
                  {/* Pagination navigation */}
                  <div className="flex items-center justify-center space-x-1 mt-3">
                    <button 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`px-2 py-1 rounded text-sm ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-orange-600 hover:bg-orange-50'}`}
                    >
                      ‹ Précédent
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded text-sm ${currentPage === pageNum ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-orange-50'}`}
                      >
                        {pageNum}
                      </button>
                    ))}
                    
                    <button 
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-2 py-1 rounded text-sm ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-orange-600 hover:bg-orange-50'}`}
                    >
                      Suivant ›
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Recommandations SEO */}
            <motion.div
              className="mt-12 bg-orange-50 rounded-xl p-6 border border-orange-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-start gap-4">
                <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span>Recommandations SEO :</span>
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                      Potentiel élevé
                    </span>
                  </h4>
                  <div className="space-y-3 text-sm text-gray-700">
                    <p className="leading-relaxed">
                      Le nombre limité de mots-clés détectés <span className="font-semibold text-orange-600">({displayKeywords.length})</span> indique un potentiel inexploité.
                      Une stratégie SEO ciblée pourrait <span className="font-semibold text-orange-600">multiplier votre visibilité par 3</span> et attirer un public
                      hautement qualifié vers votre site.
                    </p>
                    <p className="leading-relaxed">
                      En travaillant {Math.min(5, displayKeywords.length)} mots-clés non-brand, vous pourriez capter jusqu'à{' '}
                      <span className="font-semibold text-green-700 bg-green-100 px-2 py-1 rounded">{476 * 3} visiteurs supplémentaires par mois</span>{' '}
                      avec une intention réelle d'achat.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA de rappel - Nouveau */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <MidPageCTA token={token} startQuiz={startQuiz} navigate={navigate} />
          </div>
        </div>
      </section>

      {/* Section Citations IA */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-center mb-12">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                  <Brain className="w-4 h-4 mr-2" />
                  Visibilité IA
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Citations générées par l'IA
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Votre présence dans les principaux moteurs de recherche et assistants IA
                </p>
              </div>

              {/* Grid des citations IA */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                
                {/* Google */}
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Google</h3>
                        <p className="text-xs text-gray-500">Vue d'ensemble de l'IA</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-gray-900">
                        {isLoadingCitations ? '...' : (aiCitations?.google.citations || 0)}
                      </span>
                      <span className="text-sm text-gray-500">citations</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">
                          {isLoadingCitations ? '...' : (aiCitations?.google.pages || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* ChatGPT */}
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">ChatGPT</h3>
                        <p className="text-xs text-gray-500">OpenAI</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-gray-900">
                        {isLoadingCitations ? '...' : (aiCitations?.chatgpt.citations || 0)}
                      </span>
                      <span className="text-sm text-gray-500">citations</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">
                          {isLoadingCitations ? '...' : (aiCitations?.chatgpt.pages || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Perplexity */}
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Perplexity</h3>
                        <p className="text-xs text-gray-500">AI Search</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className={`text-3xl font-bold ${(aiCitations?.perplexity.citations || 0) > 0 ? 'text-cyan-600' : 'text-gray-900'}`}>
                        {isLoadingCitations ? '...' : (aiCitations?.perplexity.citations || 0)}
                      </span>
                      <span className="text-sm text-gray-500">citations</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">
                          {isLoadingCitations ? '...' : (aiCitations?.perplexity.pages || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Gemini */}
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Gemini</h3>
                        <p className="text-xs text-gray-500">Google AI</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-gray-900">
                        {isLoadingCitations ? '...' : (aiCitations?.gemini.citations || 0)}
                      </span>
                      <span className="text-sm text-gray-500">citations</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">
                          {isLoadingCitations ? '...' : (aiCitations?.gemini.pages || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Copilot */}
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Copilot</h3>
                        <p className="text-xs text-gray-500">Microsoft</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className={`text-3xl font-bold ${(aiCitations?.copilot.citations || 0) > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                        {isLoadingCitations ? '...' : (aiCitations?.copilot.citations || 0)}
                      </span>
                      <span className="text-sm text-gray-500">citations</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">
                          {isLoadingCitations ? '...' : (aiCitations?.copilot.pages || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Grok */}
                <motion.div 
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.6 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">Grok</h3>
                        <p className="text-xs text-gray-500">xAI</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-gray-900">
                        {isLoadingCitations ? '...' : (aiCitations?.grok.citations || 0)}
                      </span>
                      <span className="text-sm text-gray-500">citations</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Pages</span>
                        <span className="font-semibold text-gray-900">
                          {isLoadingCitations ? '...' : (aiCitations?.grok.pages || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>

              </div>

              {/* Info box */}
              <motion.div 
                className="mt-8 bg-orange-50 border border-orange-200 rounded-xl p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                    <Brain className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Pourquoi c'est important ?
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Les assistants IA deviennent la nouvelle porte d'entrée vers l'information. Être cité par ces plateformes 
                      augmente considérablement votre <span className="font-semibold text-orange-600">visibilité et votre crédibilité</span> auprès 
                      d'une audience qualifiée qui utilise l'IA pour ses recherches professionnelles.
                    </p>
                  </div>
                </div>
              </motion.div>

            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Plan d'action */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            <motion.div 
              className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Header */}
              <div className="p-6 md:p-8">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-4">
                    <Brain className="w-4 h-4 mr-2" />
                    Les 3 premiers pas
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                    Par quoi commencer <span className="text-orange-600">dès lundi prochain</span> ?
                  </h3>
                  <p className="text-gray-600">
                    Plan d'action personnalisé pour <span className="font-semibold text-gray-800">{siteUrl}</span>
                  </p>
                </div>

                {/* Recommandations - Limitées à 3 */}
                {isLoadingAi ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 text-orange-500 animate-spin mb-3" />
                    <p className="text-gray-500 text-sm">Génération en cours...</p>
                  </div>
                ) : (
                  <div className="space-y-4 mb-6">
                    {(aiRecommendations.length > 0 ? aiRecommendations : mockData.recommendations).slice(0, 3).map((recommendation: any, index: number) => {
                      const isStringRecommendation = typeof recommendation === 'string';
                      
                      if (isStringRecommendation) {
                        const firstSentenceEnd = recommendation.indexOf('.');
                        let title = recommendation;
                        
                        if (firstSentenceEnd !== -1) {
                          title = recommendation.substring(0, firstSentenceEnd + 1).trim();
                        }
                        
                        return (
                          <div key={index} className="border-l-4 border-orange-500 pl-4 py-3">
                            <div className="flex items-start gap-3">
                              <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                {index + 1}
                              </div>
                              <h4 className="font-semibold text-gray-900 text-sm leading-relaxed">
                                {title}
                              </h4>
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div key={index} className="border-l-4 border-orange-500 pl-4 py-3">
                            <div className="flex items-start gap-3">
                              <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                {index + 1}
                              </div>
                              <h4 className="font-semibold text-gray-900 text-sm leading-relaxed">
                                {recommendation.title || recommendation}
                              </h4>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </div>

              {/* Footer CTA Final */}
              <div className="bg-gray-800 p-6">
                <div className="text-center">
                  <motion.button 
                    className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300 inline-flex items-center"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (token) {
                        startQuiz();
                      } else {
                        navigate(ROUTES.QUIZ());
                      }
                    }}
                  >
                    <Zap className="mr-2" size={20} />
                    Obtenir mon plan personnalisé
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section de preuve sociale */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-all duration-300">
                <div className="text-center">
                  <p className="text-xl md:text-2xl font-semibold text-gray-900 mb-3">
                    Déjà plus de 400 entreprises romandes ont analysé leur site avec<br className="hidden sm:inline" /> Smart Impact.
                  </p>
                  <p className="text-gray-600 mb-8">
                    La majorité ont découvert des leviers de croissance insoupçonnés.
                  </p>
                  
                  {/* Logos des entreprises clientes */}
                  <div className="mb-8">
                    <p className="text-sm text-gray-500 mb-4">Ils nous ont fait confiance :</p>
                    <div className="grid grid-cols-3 gap-6 md:gap-8 max-w-3xl mx-auto">
                      {/* Ligne 1 */}
                      <motion.div
                        className="flex items-center justify-center h-14 md:h-16"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <img 
                          src="https://swiss-instasolar.ch/wp-content/uploads/2024/06/Logo.svg" 
                          alt="Swiss Instasolar"
                          className="max-h-11 md:max-h-14 w-auto opacity-70 hover:opacity-100 transition-opacity filter brightness-0 object-contain"
                        />
                      </motion.div>
                      <motion.div
                        className="flex items-center justify-center h-14 md:h-16"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <img 
                          src="https://smart-impact.ch/wp-content/uploads/2025/03/helvi.png" 
                          alt="Helvi"
                          className="max-h-11 md:max-h-14 w-auto opacity-70 hover:opacity-100 transition-opacity object-contain"
                        />
                      </motion.div>
                      <motion.div
                        className="flex items-center justify-center h-14 md:h-16"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <img 
                          src="https://smart-impact.ch/wp-content/uploads/2025/03/swissbo_logo.png" 
                          alt="Swissbo"
                          className="max-h-11 md:max-h-14 w-auto opacity-70 hover:opacity-100 transition-opacity filter brightness-0 object-contain"
                        />
                      </motion.div>
                      
                      {/* Ligne 2 */}
                      <motion.div
                        className="flex items-center justify-center h-14 md:h-16"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <img 
                          src="https://smart-impact.ch/wp-content/uploads/2025/03/popepoppa.webp" 
                          alt="Pope Poppa"
                          className="max-h-11 md:max-h-14 w-auto opacity-70 hover:opacity-100 transition-opacity object-contain"
                        />
                      </motion.div>
                      <motion.div
                        className="flex items-center justify-center h-14 md:h-16"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <img 
                          src="https://www.mayortraiteur.ch/wp-content/uploads/2025/04/LOGO-MAYOR-TRAITEUR-pdf-1024x1022.jpg" 
                          alt="Mayor Traiteur"
                          className="max-h-11 md:max-h-14 w-auto opacity-70 hover:opacity-100 transition-opacity object-contain"
                        />
                      </motion.div>
                      <motion.div
                        className="flex items-center justify-center h-14 md:h-16 bg-gray-800 px-3 rounded-lg"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                        <img 
                          src="https://asdfiduciaire.ch/wp-content/uploads/2025/10/White-Logo.svg" 
                          alt="ASD Fiduciaire"
                          className="max-h-9 md:max-h-11 w-auto opacity-90 hover:opacity-100 transition-opacity object-contain"
                        />
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Séparateur */}
                  <div className="border-t border-gray-200 my-6"></div>
                  
                  {/* Trust badges - Style homogène avec le reste */}
                  <div className="flex flex-wrap justify-center items-center gap-6 mb-4">
                    <div className="flex items-center gap-2 text-gray-700">
                      <div className="p-2 rounded-lg bg-orange-100">
                        <Shield className="w-5 h-5 text-orange-600" />
                      </div>
                      <span className="font-medium">100% gratuit</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <div className="p-2 rounded-lg bg-orange-100">
                        <CheckCircle className="w-5 h-5 text-orange-600" />
                      </div>
                      <span className="font-medium">Sans engagement</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <div className="p-2 rounded-lg bg-orange-100">
                        <Clock className="w-5 h-5 text-orange-600" />
                      </div>
                      <span className="font-medium">Résultats immédiats</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Quiz Completion CTA */}
            <div className="mt-8">
              <QuizCompletionCta prospect={currentProspect} />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      {currentProspect && (
        <footer className="bg-white py-8 border-t border-gray-100">
          <div className="container mx-auto px-4 text-center max-w-2xl">
            <p className="text-xs text-gray-500">
              Cette fiche est issue d'une analyse basée uniquement sur des données publiques pour {currentProspect.site_url ? extractDomain(currentProspect.site_url) : 'cette entreprise'}. 
              Elle respecte pleinement les réglementations suisses et européennes sur la protection des données.
            </p>
          </div>
        </footer>
      )}

      {/* Exit Popup */}
      {token && prospect && prospect.id && (
        <ExitPopupDemo 
          prospectId={prospect.id} 
          options={{
            sensitivity: 20,
            delay: 1000,
            onlyOnce: true
          }}
        />
      )}
    </div>
  );
};

export default TestPage2;
